import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import KPICard from "@/components/common/KPICard";
import {
  Globe, Eye, UsersThree, Broadcast, House, Trophy, FileText, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend,
} from "recharts";
import { toast } from "sonner";

const PAGE_META = {
  main: { label: "Main / Register", icon: House, color: "#f59e0b" },
  leaderboard: { label: "Leaderboard", icon: Trophy, color: "#3b82f6" },
  terms: { label: "Terms & Conditions", icon: FileText, color: "#a78bfa" },
};
const RANGE_OPTIONS = [
  { label: "7d", value: 7 },
  { label: "30d", value: 30 },
  { label: "90d", value: 90 },
];

const fmtNum = (n) => (n ?? 0).toLocaleString("en-US");
const fmtTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("all"); // all | main | leaderboard | terms
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const [sRes, rRes] = await Promise.all([
        api.get("/analytics/summary", { params: { days } }),
        api.get("/analytics/recent", {
          params: { limit: 50, ...(tab !== "all" ? { page: tab } : {}) },
        }),
      ]);
      setSummary(sRes.data);
      setRecent(rRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days, tab]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh realtime count every 30s
  useEffect(() => {
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  }, [load]);

  const activePage = tab === "all" ? null : tab;

  const chartData = useMemo(() => {
    if (!summary?.timeseries) return [];
    return summary.timeseries.map((row) => ({
      date: row.date.slice(5),
      total: row.total,
      main: row.main,
      leaderboard: row.leaderboard,
      terms: row.terms,
    }));
  }, [summary]);

  const displayedByPage = summary?.by_page || {};

  const topCountries = useMemo(() => {
    if (!summary) return [];
    if (activePage) {
      return (displayedByPage[activePage]?.top_countries || []).slice(0, 10);
    }
    return (summary.top_countries || []).slice(0, 10);
  }, [summary, activePage, displayedByPage]);

  const activeVisits = activePage
    ? displayedByPage[activePage]?.visits || 0
    : summary?.total?.visits || 0;
  const activeUnique = activePage
    ? displayedByPage[activePage]?.unique_visitors || 0
    : summary?.total?.unique_visitors || 0;

  if (loading) return <div className="p-8 text-gray-400" data-testid="analytics-loading">Loading analytics…</div>;
  if (!summary) return <div className="p-8 text-red-400">Failed to load analytics</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto" data-testid="analytics-page">
      <PageHeader
        title="Website Analytics"
        subtitle={`Visitor tracking · ${summary.days}-day window · resolves country via ipapi.co`}
        breadcrumbs="Dashboard / Website Analytics"
        testId="analytics-header"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-mono text-emerald-300" data-testid="realtime-count">
                {summary.total.realtime} LIVE
              </span>
            </div>
            <div className="inline-flex rounded-md border border-white/10 overflow-hidden">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  data-testid={`range-${r.value}`}
                  onClick={() => setDays(r.value)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    days === r.value
                      ? "bg-blue-500/20 text-blue-300"
                      : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              data-testid="analytics-refresh"
              onClick={() => load()}
              className="p-2 rounded-md border border-white/10 text-gray-400 hover:text-white hover:bg-white/5"
              title="Refresh"
            >
              <ArrowsClockwise size={16} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          testId="kpi-total-visits"
          title="Total Visits"
          value={fmtNum(summary.total.visits)}
          subtitle={`${activePage ? PAGE_META[activePage].label : "All 3 public pages"}`}
          icon={<Eye weight="duotone" size={20} />}
          accent="blue"
        />
        <KPICard
          testId="kpi-unique-visitors"
          title="Unique Visitors"
          value={fmtNum(summary.total.unique_visitors)}
          subtitle={`Distinct IPs · last ${summary.days}d`}
          icon={<UsersThree weight="duotone" size={20} />}
          accent="orange"
        />
        <KPICard
          testId="kpi-realtime"
          title="Live Now (5 min)"
          value={fmtNum(summary.total.realtime)}
          subtitle="Auto-refresh every 30s"
          icon={<Broadcast weight="duotone" size={20} />}
          accent="green"
        />
        <KPICard
          testId="kpi-countries"
          title="Countries Reached"
          value={fmtNum(summary.top_countries.length)}
          subtitle={summary.top_countries[0] ? `Top: ${summary.top_countries[0].country}` : "-"}
          icon={<Globe weight="duotone" size={20} />}
          accent="purple"
        />
      </div>

      {/* Per-page tabs */}
      <div className="mb-6 flex flex-wrap gap-2" data-testid="page-tabs">
        {[
          { id: "all", label: "All Pages", icon: Eye },
          { id: "main", label: PAGE_META.main.label, icon: House },
          { id: "leaderboard", label: PAGE_META.leaderboard.label, icon: Trophy },
          { id: "terms", label: PAGE_META.terms.label, icon: FileText },
        ].map(({ id, label, icon: Icon }) => {
          const active = tab === id;
          const visits = id === "all" ? summary.total.visits : displayedByPage[id]?.visits || 0;
          return (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                active
                  ? "bg-blue-500/15 text-white border-blue-500/40"
                  : "border-white/10 text-gray-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon weight="duotone" size={16} />
              <span>{label}</span>
              <span className="font-mono text-[11px] bg-white/5 px-1.5 py-0.5 rounded">
                {fmtNum(visits)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Timeseries */}
        <div className="lg:col-span-2 bg-[#111827] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Daily Traffic</p>
              <p className="font-display text-lg font-bold text-white">
                Visits over {summary.days} days
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Filter</p>
              <p className="text-sm text-white">{activePage ? PAGE_META[activePage].label : "All pages stacked"}</p>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }} data-testid="analytics-timeseries">
            <ResponsiveContainer>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={PAGE_META.main.color} stopOpacity={0.5} />
                    <stop offset="95%" stopColor={PAGE_META.main.color} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gLead" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={PAGE_META.leaderboard.color} stopOpacity={0.5} />
                    <stop offset="95%" stopColor={PAGE_META.leaderboard.color} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gTerms" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={PAGE_META.terms.color} stopOpacity={0.5} />
                    <stop offset="95%" stopColor={PAGE_META.terms.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} />
                <YAxis stroke="#6b7280" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#0B0F19", border: "1px solid #1f2937", borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: "#9ca3af" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {(!activePage || activePage === "main") && (
                  <Area type="monotone" dataKey="main" stackId="1" stroke={PAGE_META.main.color} fill="url(#gMain)" name={PAGE_META.main.label} />
                )}
                {(!activePage || activePage === "leaderboard") && (
                  <Area type="monotone" dataKey="leaderboard" stackId="1" stroke={PAGE_META.leaderboard.color} fill="url(#gLead)" name={PAGE_META.leaderboard.label} />
                )}
                {(!activePage || activePage === "terms") && (
                  <Area type="monotone" dataKey="terms" stackId="1" stroke={PAGE_META.terms.color} fill="url(#gTerms)" name={PAGE_META.terms.label} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Country breakdown */}
        <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Top Countries</p>
          <p className="font-display text-lg font-bold text-white mb-4">
            {activePage ? PAGE_META[activePage].label : "All Pages"}
          </p>
          <div style={{ width: "100%", height: 280 }} data-testid="country-chart">
            {topCountries.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                No data yet
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={topCountries} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis type="number" stroke="#6b7280" fontSize={11} allowDecimals={false} />
                  <YAxis type="category" dataKey="country" stroke="#6b7280" fontSize={11} width={100} />
                  <Tooltip
                    contentStyle={{ background: "#0B0F19", border: "1px solid #1f2937", borderRadius: 6, fontSize: 12 }}
                    labelStyle={{ color: "#9ca3af" }}
                  />
                  <Bar dataKey="visits" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Per-page split table */}
      <div className="bg-[#111827] border border-white/10 rounded-lg p-5 mb-6">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Statistics per Page</p>
        <p className="font-display text-lg font-bold text-white mb-4">Page Breakdown</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["main", "leaderboard", "terms"].map((p) => {
            const meta = PAGE_META[p];
            const stat = displayedByPage[p] || { visits: 0, unique_visitors: 0, top_countries: [] };
            const Icon = meta.icon;
            return (
              <div
                key={p}
                data-testid={`page-stat-${p}`}
                className="p-4 rounded-md border border-white/10 bg-[#0B0F19]"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon weight="duotone" size={18} style={{ color: meta.color }} />
                  <p className="text-sm font-semibold text-white">{meta.label}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <p className="text-[10px] uppercase text-gray-500">Visits</p>
                    <p className="font-mono text-xl font-bold text-white">{fmtNum(stat.visits)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-gray-500">Unique</p>
                    <p className="font-mono text-xl font-bold text-white">{fmtNum(stat.unique_visitors)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-gray-500 mb-1">Top 3 Countries</p>
                  <div className="flex flex-wrap gap-1">
                    {(stat.top_countries || []).slice(0, 3).map((c) => (
                      <span key={c.country} className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-gray-300">
                        {c.country} <span className="font-mono text-gray-500">·{c.visits}</span>
                      </span>
                    ))}
                    {(!stat.top_countries || stat.top_countries.length === 0) && (
                      <span className="text-[11px] text-gray-500">No visits yet</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent visits */}
      <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Live Feed</p>
            <p className="font-display text-lg font-bold text-white">
              Recent Visits{activePage ? ` · ${PAGE_META[activePage].label}` : ""}
            </p>
          </div>
          <span className="text-xs text-gray-500 font-mono">{recent.length} shown</span>
        </div>
        <div className="overflow-x-auto" data-testid="recent-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-white/10">
                <th className="py-2 pr-4">Timestamp (UTC)</th>
                <th className="py-2 pr-4">Page</th>
                <th className="py-2 pr-4">Country</th>
                <th className="py-2 pr-4">Referrer</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-500">
                    No visits yet — waiting for the first tracked page view.
                  </td>
                </tr>
              )}
              {recent.map((v, idx) => {
                const meta = PAGE_META[v.page];
                return (
                  <tr key={v.id || idx} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2 pr-4 font-mono text-xs text-gray-300 whitespace-nowrap">
                      {fmtTime(v.timestamp)}
                    </td>
                    <td className="py-2 pr-4">
                      {meta ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px]"
                          style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
                        >
                          {meta.label}
                        </span>
                      ) : (v.page || "-")}
                    </td>
                    <td className="py-2 pr-4 text-gray-300">
                      {v.country_code && v.country_code !== "LO" && (
                        <span className="font-mono text-[10px] text-gray-500 mr-1">{v.country_code}</span>
                      )}
                      {v.country_name || "Unknown"}
                    </td>
                    <td className="py-2 pr-4 text-xs text-gray-500 max-w-[280px] truncate">
                      {v.referrer || "direct"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
